import { useEffect, useRef, useState } from 'react';
import type { SettingListOption } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SettingListSelect } from './SettingListSelect';
import { eveningPartLabel } from './planParts';

export interface PlanPartValues {
  eveningPart: string | null;
  startTime: string | null;
}

export interface NewPartPlanValues extends PlanPartValues {
  name: string;
  /** Start from a copy of the plan on screen, rather than the empty room. */
  copyCurrent: boolean;
}

type PlanPartModalProps = {
  open: boolean;
  onClose: () => void;
  parts: readonly SettingListOption[];
  initialPart: string | null;
  initialTime: string | null;
  isSaving: boolean;
} & (
  | { mode: 'edit'; onSave: (values: PlanPartValues) => void }
  | {
      mode: 'new';
      /** A plan is on screen to copy from. */
      canCopy: boolean;
      onSave: (values: NewPartPlanValues) => void;
    }
);

/**
 * Which part of the evening a plan lays out and when it begins — for the plan
 * on screen (`edit`), or a new plan for another part (`new`), started from a
 * copy of this one (the room as it stands, to rearrange) or from the empty
 * room. A new plan is named after its part until a name is typed.
 */
export function PlanPartModal(props: PlanPartModalProps) {
  const { open, onClose, parts, initialPart, initialTime, isSaving, mode } = props;
  const canCopy = props.mode === 'new' && props.canCopy;
  const [part, setPart] = useState<string | null>(initialPart);
  const [time, setTime] = useState(initialTime ?? '');
  const [name, setName] = useState('');
  const [nameTyped, setNameTyped] = useState(false);
  const [copyCurrent, setCopyCurrent] = useState(true);

  // Filled in when it opens, and never after: the suggested part can change
  // as plans or Settings finish loading, and that must not wipe what has
  // been typed.
  const initial = useRef({ initialPart, initialTime, parts, canCopy });
  initial.current = { initialPart, initialTime, parts, canCopy };
  useEffect(() => {
    if (!open) return;
    const {
      initialPart: part0,
      initialTime: time0,
      parts: parts0,
      canCopy: copyable0,
    } = initial.current;
    setPart(part0);
    setTime(time0 ?? '');
    setName(eveningPartLabel(part0, parts0) ?? '');
    setNameTyped(false);
    // A copy is the default only when it was on offer as the dialog opened:
    // one that becomes available later (the plan finished loading) must not
    // quietly replace the empty room the producer was shown.
    setCopyCurrent(copyable0);
  }, [open]);

  const choosePart = (value: string | null) => {
    setPart(value);
    if (!nameTyped) setName(eveningPartLabel(value, parts) ?? '');
  };

  const values: PlanPartValues = { eveningPart: part, startTime: time || null };
  const ready = mode === 'edit' || !!name.trim();
  const save = () => {
    if (!ready || isSaving) return;
    if (props.mode === 'edit') props.onSave(values);
    else props.onSave({ ...values, name: name.trim(), copyCurrent: canCopy && copyCurrent });
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={mode === 'edit' ? 'Part of the evening' : 'Lay out another part'}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!ready || isSaving}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
            data-testid="plan-part-save-btn"
          >
            {isSaving ? 'Saving…' : mode === 'edit' ? 'Save' : 'Create plan'}
          </button>
        </div>
      }
    >
      <div className="space-y-3" data-testid="plan-part-modal">
        <SettingListSelect
          options={parts}
          value={part}
          onChange={choosePart}
          label="Part of the evening"
          emptyMessage="No parts yet — add them in Settings → App Settings"
          testId="plan-part-select"
        />
        <div>
          <label className="block text-xs text-gray-500 mb-1" htmlFor="plan-part-time">
            Starts at
          </label>
          <input
            id="plan-part-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="plan-part-time"
          />
        </div>
        {mode === 'new' && (
          <>
            <div>
              <label className="block text-xs text-gray-500 mb-1" htmlFor="plan-part-name">
                Plan name
              </label>
              <input
                id="plan-part-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameTyped(true);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="plan-part-name"
              />
            </div>
            {canCopy && (
              <fieldset className="space-y-1">
                <legend className="text-xs text-gray-500 mb-1">Start from</legend>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="plan-part-start"
                    checked={copyCurrent}
                    onChange={() => setCopyCurrent(true)}
                    data-testid="plan-part-copy"
                  />
                  A copy of this plan, to rearrange
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="plan-part-start"
                    checked={!copyCurrent}
                    onChange={() => setCopyCurrent(false)}
                    data-testid="plan-part-empty"
                  />
                  The empty room
                </label>
              </fieldset>
            )}
          </>
        )}
      </div>
    </EntityModal>
  );
}
